// AssetDetail.jsx — the self-contained asset feature: in-place search,
// compact price chart, persisted price target, news, drill slide-over,
// and the "Ativo" workbench. No navigation — everything drills in place.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { AreaChart, Area, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Search, X, ArrowLeft, Target } from 'lucide-react';
import {
  API_URL, DINO_URL, NEWS_URL,
  fmtNum, fmtVol, fmtAssetPrice, baseSymbol, readTarget, writeTarget,
} from './rrtvFormat';
import { TickerLogo } from './TickerLogo';

// ---------- AssetSearch — Header's search UX, but selects in place (no navigate) ----------
export function AssetSearch({ marketData, onSelect, autoFocus, placeholder = 'Buscar ativo por nome ou ticker…' }) {
  const [query,    setQuery]    = useState('');
  const [results,  setResults]  = useState([]);
  const [open,     setOpen]     = useState(false);
  const [sel,      setSel]      = useState(0);
  const [universe, setUniverse] = useState([]); // state (not ref) so results refresh once loaded
  const inputRef = useRef(null);
  const wrapRef  = useRef(null);

  // Universe: B3 (from marketData) + worldwide (DINO) — same source as the header
  useEffect(() => {
    const b3 = (marketData?.allTickers || []).map(s => ({
      ticker: s.ticker, name: s.name || s.ticker, price: s.ult, change: s.var, currency: 'BRL', category: '📈 B3',
    }));
    let alive = true;
    setUniverse(b3); // show B3 immediately; world list merges in when it lands
    fetch(`${DINO_URL}/api/tickers`)
      .then(r => r.json())
      .then(world => {
        if (!alive) return;
        const arr = Array.isArray(world) ? world : (world?.tickers || []);
        const seen = new Set(b3.map(x => x.ticker));
        const worldMapped = arr.filter(t => !seen.has(t.ticker)).map(t => ({
          ticker: t.ticker, name: t.name || t.ticker, price: t.ult, change: t.var,
          currency: t.currency, category: `🌍 ${t.exchange || 'Mundial'}`,
        }));
        setUniverse([...b3, ...worldMapped]);
      })
      .catch(() => { if (alive) setUniverse(b3); });
    return () => { alive = false; };
  }, [marketData]);

  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) { setResults([]); setSel(0); return; }
    const r = universe
      .filter(a => a.ticker.toLowerCase().includes(q) || (a.name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.ticker.toLowerCase() === q ? -1 : b.ticker.toLowerCase() === q ? 1 : 0))
      .slice(0, 8);
    setResults(r); setSel(0);
  }, [query, universe]);

  const pick = (item) => { onSelect(item); setOpen(false); setQuery(''); };
  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(p => Math.min(p + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(p => Math.max(p - 1, 0)); }
    else if (e.key === 'Enter' && results[sel]) pick(results[sel]);
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="rrtv-asearch" ref={wrapRef}>
      <div className="rrtv-asearch-box">
        <Search size={13} className="rrtv-asearch-icon" />
        <input ref={inputRef} className="rrtv-asearch-input" value={query} placeholder={placeholder}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onKeyDown={onKey} />
        {query && <button className="rrtv-asearch-clear" onClick={() => { setQuery(''); inputRef.current?.focus(); }}><X size={12} /></button>}
      </div>
      {open && query && (
        <div className="rrtv-asearch-drop">
          {results.length === 0
            ? <div className="rrtv-asearch-empty">Nenhum resultado para "{query}"</div>
            : results.map((r, i) => {
                const up = (r.change ?? 0) >= 0;
                return (
                  <div key={r.ticker} className={`rrtv-asearch-row${i === sel ? ' sel' : ''}`}
                    onMouseEnter={() => setSel(i)} onClick={() => pick(r)} role="button" tabIndex={0}>
                    <TickerLogo ticker={r.ticker} size={18} />
                    <div className="rrtv-asearch-row-id">
                      <span className="rrtv-asearch-row-tk">{r.ticker}</span>
                      <span className="rrtv-asearch-row-nm">{r.name}</span>
                    </div>
                    <span className="rrtv-asearch-row-cat">{r.category}</span>
                    {r.change != null && (
                      <span className={`rrtv-asearch-row-var ${up ? 'pos' : 'neg'}`}>{up ? '+' : ''}{Number(r.change).toFixed(2)}%</span>
                    )}
                  </div>
                );
              })}
        </div>
      )}
    </div>
  );
}

// ---------- AssetMiniChart — compact price series with range controls (AssetPage UX) ----------
const ASSET_RANGES = [
  { label: '1M', days: 30 }, { label: '3M', days: 90 }, { label: '6M', days: 180 },
  { label: '1A', days: 365 }, { label: 'MAX', days: 'max' },
];

function AssetMiniChart({ ticker }) {
  const [all,     setAll]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [range,   setRange]   = useState(90);

  useEffect(() => {
    let alive = true;
    setLoading(true); setAll([]);
    fetch(`${API_URL}/api/yahoo/v2/${ticker}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!alive) return;
        const rows = (d?.candles || []).map(c => ({ ts: c.ts, close: c.close })).filter(c => c.close != null);
        setAll(rows); setLoading(false);
      })
      .catch(() => { if (alive) { setAll([]); setLoading(false); } });
    return () => { alive = false; };
  }, [ticker]);

  const data = useMemo(() => {
    if (!all.length) return [];
    if (range === 'max') return all;
    const cutoff = Math.floor(Date.now() / 1000) - range * 86400;
    return all.filter(d => d.ts >= cutoff);
  }, [all, range]);

  const first = data[0]?.close, last = data[data.length - 1]?.close;
  const up = first != null && last != null ? last >= first : true;
  const color = up ? '#22c55e' : '#DC143C';
  const gradId = `ad-grad-${baseSymbol(ticker)}`;

  return (
    <div className="rrtv-ad-chart">
      <div className="rrtv-ad-ranges">
        {ASSET_RANGES.map(r => (
          <button key={r.label} className={`rrtv-ad-range${range === r.days ? ' active' : ''}`}
            onClick={() => setRange(r.days)}>{r.label}</button>
        ))}
      </div>
      <div className="rrtv-ad-chart-area">
        {loading ? (
          <div className="rrtv-ad-chart-empty">Carregando série…</div>
        ) : data.length < 2 ? (
          <div className="rrtv-ad-chart-empty">Sem histórico disponível.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <YAxis domain={['dataMin', 'dataMax']} hide />
              <Tooltip contentStyle={{ background: '#12111f', border: '1px solid rgba(102,96,254,0.3)', borderRadius: 6, fontSize: 11, color: '#fff' }}
                labelFormatter={() => ''} formatter={(v) => [fmtNum(v), 'Fecham.']} />
              <Area type="monotone" dataKey="close" stroke={color} strokeWidth={1.6} fill={`url(#${gradId})`} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ---------- AssetTargetRow — set your own price target (persisted) ----------
function AssetTargetRow({ ticker, price, currency }) {
  const [target, setTarget] = useState(() => readTarget(ticker));
  const [draft,  setDraft]  = useState(target != null ? String(target) : '');

  useEffect(() => { const t = readTarget(ticker); setTarget(t); setDraft(t != null ? String(t) : ''); }, [ticker]);

  const save = () => {
    const v = parseFloat(String(draft).replace(',', '.'));
    if (isNaN(v)) { writeTarget(ticker, null); setTarget(null); }
    else { writeTarget(ticker, v); setTarget(v); }
  };
  const clear = () => { writeTarget(ticker, null); setTarget(null); setDraft(''); };

  const dist = target != null && price != null ? ((target - price) / price) * 100 : null;

  return (
    <div className="rrtv-ad-target">
      <div className="rrtv-ad-target-head">
        <span className="rrtv-ad-target-l"><Target size={11} /> MEU PREÇO-ALVO</span>
        {target != null && <button className="rrtv-ad-target-clear" onClick={clear}>limpar</button>}
      </div>
      <div className="rrtv-ad-target-row">
        <input className="rrtv-ad-target-input" inputMode="decimal" value={draft}
          placeholder={price != null ? fmtNum(price) : '0,00'}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); }} />
        <button className="rrtv-ad-target-set" onClick={save}>Definir</button>
      </div>
      {target != null && dist != null && (
        <div className={`rrtv-ad-target-dist ${dist >= 0 ? 'up' : 'down'}`}>
          alvo {fmtAssetPrice(target, currency)} · {dist >= 0 ? `faltam +${dist.toFixed(2)}% para atingir` : `${Math.abs(dist).toFixed(2)}% acima do alvo`}
        </div>
      )}
    </div>
  );
}

function AdStat({ l, v, cls }) {
  return (
    <div className="rrtv-drill-stat">
      <div className="rrtv-drill-stat-l">{l}</div>
      <div className={`rrtv-drill-stat-v ${cls || ''}`.trim()}>{v}</div>
    </div>
  );
}

// ---------- AssetDetail — self-contained expanded view (no navigation) ----------
export function AssetDetail({ asset }) {
  const ticker = asset?.ticker;
  // Full symbol (keeps .SA/.T/.HK/.L…) — data APIs need it; the board strips it for display.
  const fetchTicker = asset?.fullTicker || ticker;
  const [quote, setQuote] = useState(asset || null);
  const [news,  setNews]  = useState([]);

  useEffect(() => { setQuote(asset || null); }, [asset]);

  useEffect(() => {
    if (!fetchTicker) return;
    let alive = true;
    (async () => {
      try {
        let r = await fetch(`${API_URL}/api/ticker/${fetchTicker}`);
        if (!r.ok) r = await fetch(`${DINO_URL}/api/ticker/${fetchTicker}`);
        if (r.ok) { const d = await r.json(); if (alive && d) setQuote(q => ({ ...q, ...d })); }
      } catch {}
    })();
    (async () => {
      try {
        const r = await fetch(`${NEWS_URL}/api/news/ticker/${baseSymbol(fetchTicker)}`);
        if (r.ok) { const d = await r.json(); if (alive) setNews((d.news || []).slice(0, 5)); }
      } catch {}
    })();
    return () => { alive = false; };
  }, [fetchTicker]);

  if (!asset) {
    return (
      <div className="rrtv-ad-empty">
        <Search size={26} />
        <p>Busque um ativo para abrir a visão detalhada aqui mesmo.</p>
      </div>
    );
  }

  const up    = (quote?.var ?? quote?.change ?? 0) >= 0;
  const chg   = quote?.var ?? quote?.change ?? 0;
  const price = quote?.ult ?? quote?.price;
  const isB3  = !fetchTicker.includes('.') || fetchTicker.endsWith('.SA');
  const ccy   = quote?.currency || (isB3 ? 'BRL' : null);

  return (
    <div className="rrtv-ad">
      <div className="rrtv-ad-top">
        <TickerLogo ticker={ticker} size={40} className="rrtv-drill-logo" />
        <div className="rrtv-ad-id">
          <div className="rrtv-ad-tk">{ticker}</div>
          <div className="rrtv-ad-nm">{quote?.name || baseSymbol(ticker)}</div>
        </div>
        <div className="rrtv-ad-quote">
          <div className="rrtv-ad-price">{fmtAssetPrice(price, ccy)}</div>
          <div className={`rrtv-ad-var ${up ? 'pos' : 'neg'}`}>{up ? '▲ +' : '▼ '}{Math.abs(chg).toFixed(2)}%</div>
        </div>
      </div>

      <AssetMiniChart ticker={fetchTicker} />

      <div className="rrtv-ad-stats">
        <AdStat l="ÚLTIMO"  v={fmtAssetPrice(price, ccy)} />
        <AdStat l="VAR DIA" v={`${up ? '+' : ''}${chg.toFixed(2)}%`} cls={up ? 'pos' : 'neg'} />
        <AdStat l="VOLUME"  v={fmtVol(quote?.vol)} />
        <AdStat l="MOEDA"   v={ccy || '—'} />
      </div>

      <AssetTargetRow ticker={fetchTicker} price={price} currency={ccy} />

      <div className="rrtv-ad-news">
        <div className="rrtv-ad-news-h">NOTÍCIAS · {baseSymbol(ticker)}</div>
        {news.length === 0
          ? <div className="rrtv-ad-news-empty">Sem cobertura recente para este ativo.</div>
          : news.map((n, i) => (
              <a key={n.id || n.url || n.link || i} className="rrtv-ad-news-row"
                href={n.url || n.link || '#'} target="_blank" rel="noopener noreferrer">
                <span className="rrtv-ad-news-i">{String(i + 1).padStart(2, '0')}</span>
                <span className="rrtv-ad-news-t">
                  {n.title || n.headline || '—'}
                  {n.source && <em> · {n.source}</em>}
                </span>
              </a>
            ))}
      </div>

      <a className="rrtv-drill-cta"
        href={`https://magma.relatorioreservado.com.br/asset/stock/${encodeURIComponent(fetchTicker)}`}
        target="_blank" rel="noopener noreferrer">
        Abrir página completa do ativo →
      </a>
    </div>
  );
}

// ---------- AssetDrill — click-to-drill slide-over (freezes the feed) ----------
export function AssetDrill({ asset, marketData, onClose }) {
  const [current, setCurrent] = useState(asset);
  useEffect(() => { setCurrent(asset); }, [asset]);
  return (
    <>
      <div className="rrtv-drill-scrim" onClick={onClose} />
      <aside className="rrtv-drill rrtv-drill-wide" role="dialog" aria-label={`Detalhe ${current?.ticker || ''}`}>
        <header className="rrtv-drill-head">
          <button className="rrtv-drill-back" onClick={onClose} aria-label="Voltar"><ArrowLeft size={16} /> Voltar</button>
          <button className="rrtv-drill-x" onClick={onClose} aria-label="Fechar"><X size={16} /></button>
        </header>
        <div className="rrtv-drill-search">
          <div className="rrtv-drill-search-label"><Search size={11} /> TROCAR ATIVO</div>
          <AssetSearch marketData={marketData} onSelect={setCurrent} placeholder="Ex.: NVDA, PETR4, Apple…" />
        </div>
        <div className="rrtv-drill-scroll">
          <AssetDetail asset={current} />
        </div>
      </aside>
    </>
  );
}

// ---------- AssetWorkbench — a customizable "Ativo" tab: search → self-contained detail ----------
export function AssetWorkbench({ marketData }) {
  const [asset, setAsset] = useState(null);
  return (
    <div className="rrtv-workbench">
      <div className="rrtv-workbench-head">
        <div className="rrtv-workbench-title">
          <h2 className="rrtv-research-h">Ativo</h2>
          <p className="rrtv-research-sub">Busque, abra a visão detalhada e defina seu preço-alvo — tudo nesta aba.</p>
        </div>
        <div className="rrtv-workbench-search">
          <AssetSearch marketData={marketData} onSelect={setAsset} autoFocus />
        </div>
      </div>
      <AssetDetail asset={asset} />
    </div>
  );
}
